// ==========================================================================
// lib/wfh.ts — work-from-home days (FF-007, 9 Oct 2026)
// One row per person per day. Untick = soft delete; re-tick = new row
// (the unique index ignores deleted rows).
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useHouseholdId } from "./household";

export type WfhDay = { id: string; person_id: string; work_date: string; hours: number; employer_entity_id: string | null };

export function useWfhDays(personId: string | undefined, from: string, to: string) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["wfh", hid, personId, from, to], enabled: !!hid && !!personId,
    queryFn: async () => {
      const { data, error } = await supabase.from("wfh_days").select("id,person_id,work_date,hours,employer_entity_id")
        .eq("person_id", personId!).gte("work_date", from).lte("work_date", to).is("deleted_at", null).order("work_date");
      if (error) throw error;
      return (data as WfhDay[]).map((d) => ({ ...d, hours: Number(d.hours) }));
    },
  });
}

export function useToggleWfh() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: { existing?: WfhDay; personId: string; date: string; hours: number; employerId: string | null }) => {
      if (a.existing) {
        const { error } = await supabase.from("wfh_days").update({ deleted_at: new Date().toISOString() }).eq("id", a.existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("wfh_days").insert({ household_id: hid, person_id: a.personId, work_date: a.date, hours: a.hours, employer_entity_id: a.employerId });
        if (error) throw error;
      }
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["wfh", hid] }); void qc.invalidateQueries({ queryKey: ["audit", hid] }); },
  });
}

export function useSetWfhHours() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: { id: string; hours: number }) => {
      const { error } = await supabase.from("wfh_days").update({ hours: a.hours }).eq("id", a.id);
      if (error) throw error;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["wfh", hid] }); },
  });
}
